import { useEffect, useRef, useState } from "react";
import { Box, Alert, Spinner, Text } from "@chakra-ui/react";

import MobileBottomSheet from "../racks/RackObservation/MobileBottomSheet";
import ImageAnnotationSection from "../racks/RackObservation/Form/ImageAnnotationSection";
import { Annotation, useAnnotator } from "@annotorious/react";
import { PageContent, PageHeader, PageLayout } from "@/components/page";
import ObservationSubHeader from "../racks/RackObservation/Form/ObservationSubHeader";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { MdKeyboardArrowLeft } from "react-icons/md";

type CountResultsProps = {
  image: File;
  annotations: Annotation[];
  isLoading: boolean;
  error?: Error | null;
};

function CountResults({ image, annotations, isLoading, error }: CountResultsProps) {
  const navigate = useNavigate();
  const drawerElement = useRef<HTMLDivElement>(null);
  const snapPoints = [0.15, 0.5, 1];
  const [drawerHeight, setDrawerHeight] = useState<number>(snapPoints[1]);
  const anno = useAnnotator();
  useEffect(() => {
    if (anno && annotations) anno.setAnnotations(annotations);
  }, [anno, annotations]);
  const dateStr = new Date().toLocaleString([], {
    dateStyle: "short",
    timeStyle: "short",
  });

  return (
    <PageLayout>
      <PageHeader
        backLink={-1}
        bg={{ base: "black/75", sm: "white" }}
        color={{ base: "white", sm: "initial" }}
        borderBottomColor={{ base: "black", sm: "border" }}
        mb={0}
      >
        Rack Count {dateStr}
      </PageHeader>
      <PageContent fluid p={0} gridRow="2 / -1" maxW="none">
        <Box
          display={{ base: "flex", md: "grid" }}
          flexDirection="column"
          gridTemplateColumns={{
            base: "minmax(0px, 1fr)",
            md: "minmax(0px, 1fr) 20rem",
          }}
          gridTemplateRows="minmax(0px, max-content) minmax(0px, 1fr)"
          gap={0}
          as="form"
          height="100%"
          minH="calc(100vh - 3.3rem)"
          maxH="calc(100vh - 3.3rem)"
          overflow="hidden"
          ref={drawerElement}
          position="relative"
        >
          <ObservationSubHeader
            title={`Rack Count ${dateStr}`}
            backLink={-1}
          />
          <ImageAnnotationSection
            drawerHeight={drawerHeight}
            snapPoints={snapPoints}
            image={image!}
            editMode={false}
            updateMode={false}
            annotations={annotations}
            selectedAnnotations={[]}
            showBadges={false}
            enablePopups={false}
          />
          <MobileBottomSheet
            drawerContainerRef={drawerElement.current}
            setDrawerHeight={setDrawerHeight}
            drawerHeight={drawerHeight}
            snapPoints={snapPoints}
          >
            <Box
              textAlign="center"
              bg="bg.warning"
              borderColor="orange.muted"
              borderWidth={1}
              rounded="md"
              p={1}
              mb={3}
            >
              <Text fontSize="sm" fontWeight={600}>
                Rapid inventory count - not saved to rack
              </Text>
            </Box>
            {isLoading && (
              <Alert.Root
                borderStartWidth="3px"
                borderStartColor="colorPalette.600"
                title="Counting fragments..."
              >
                <Alert.Indicator>
                  <Spinner size="sm" />
                </Alert.Indicator>
                <Alert.Title>Counting fragments...</Alert.Title>
              </Alert.Root>
            )}
            {annotations.length > 0 && <Text fontSize="x-large" fontWeight={600}>{annotations.length} Fragments</Text>}
            {error && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Title>It was not possible to count the fragments.</Alert.Title>
              </Alert.Root>
            )}
            <Button onClick={() => navigate(-1)} variant="outline" colorPalette="blue" width="full" mt={4}>
              <MdKeyboardArrowLeft color="blue.500" />
              Back
            </Button>
          </MobileBottomSheet>
        </Box>
      </PageContent>
    </PageLayout>
  );
}

export default CountResults;
